/**
 * Event detail shell (`/me/events/:id/*`).
 *
 * One route load, four child pages. The layout owns everything the children
 * would otherwise fetch or repeat: the event itself is loaded once here and
 * handed down through the outlet context, the header carries the breadcrumb
 * trail, and the share link lives above the tabs because every child page may
 * need to hand it out. The header also carries the direct entry to the share
 * page, since the organizer console has no other way to reach it.
 *
 * The share link carries the organiser's referral code: whoever opens it and
 * registers is attributed to this account, which the organiser is never told
 * about on this page.
 */

import { useState } from 'react'
import { ListChecks, Settings2, Sparkles, Users } from 'lucide-react'
import { Link, Navigate, Outlet, useOutletContext, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { EventDetailDto } from '@shared/types'

import { PageHeader } from '../../../components/layout/page-header'
import { ErrorBlock, SkeletonCard } from '../../../components/layout/states'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { useCurrentUser } from '../../../hooks/use-auth'
import { useEventDetail } from '../../../hooks/use-events'
import { copyText } from '../../../lib/clipboard'
import { formatUnixSeconds, shareUrl } from '../../../lib/format'
import { EventStatusBadge } from '../../../components/event/event-cards'
import { currentLocale } from '../../../lib/i18n'

/** What every child page receives from the layout outlet. */
export interface EventDetailContextValue {
  event: EventDetailDto
}

/**
 * Reads the loaded event from the outlet context.
 *
 * The child pages are only ever mounted under this layout, so a missing context
 * is a routing mistake rather than a user-visible state.
 */
export function useEventDetailContext(): EventDetailContextValue {
  const context = useOutletContext<EventDetailContextValue | null>()
  if (!context) {
    throw new Error('EventDetail pages must be rendered inside EventDetailLayout')
  }
  return context
}

export function EventDetailLayout() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const detail = useEventDetail(id)
  const { user } = useCurrentUser()
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const locale = currentLocale()
  const event = detail.data?.event

  if (!id) {
    return (
      <div className="container-page">
        <ErrorBlock title={t('common.notFound')} retryLabel={t('common.retry')} />
      </div>
    )
  }

  if (detail.isPending) {
    return (
      <div className="container-page flex flex-col gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (detail.isError || !event) {
    return (
      <div className="container-page">
        <ErrorBlock
          title={t('error.not_found')}
          retryLabel={t('common.retry')}
          onRetry={() => void detail.refetch()}
        />
      </div>
    )
  }

  // This is the organizer's console: every action it offers is creator-only, so a
  // member is sent to the share page, where they read the same outing and can do
  // everything a member is allowed to do.
  if (user === null || event.creatorId !== user.id) {
    return <Navigate to={`/e/${event.shareToken}`} replace />
  }

  const childRoutes = [
    { to: `/me/events/${event.id}/basic`, icon: Settings2, label: t('events.basic') },
    { to: `/me/events/${event.id}/activities`, icon: ListChecks, label: t('events.activities') },
    { to: `/me/events/${event.id}/participants`, icon: Users, label: t('events.participants') },
    { to: `/me/events/${event.id}/recommend`, icon: Sparkles, label: t('events.recommend') },
  ]
  const shareLink = shareUrl(event.shareToken, user?.referralCode)

  /** Copies the link and reports the outcome next to the button. */
  async function copyShareLink() {
    setCopyState((await copyText(shareLink)) ? 'copied' : 'failed')
  }

  return (
    <div className="container-page flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('nav.me'), to: '/me' },
          { label: t('nav.events'), to: '/me/events' },
          { label: event.title },
        ]}
        title={event.title}
        description={t('events.createdAt', { date: formatUnixSeconds(event.createdAt, locale) })}
        actions={
          // The activity page is a page-level destination, so it sits next to the
          // status rather than inside the share card, where the first reader looked
          // for it last. `PageHeader` already lays its actions out in a wrapping row.
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/e/${event.shareToken}`}>{t('events.openActivityPage')}</Link>
            </Button>
            <EventStatusBadge status={event.status} />
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('events.shareLink')}</CardTitle>
          <CardDescription>
            {event.status === 'archived' ? t('events.shareHintArchived') : t('events.shareHint')}
          </CardDescription>
        </CardHeader>
        {/*
          The address and its copy button share one line. Wrapped, the address
          broke over three lines and made the card the tallest thing on the page;
          the button is the way it is handed out, so the address may truncate here.
        */}
        <CardContent className="flex min-w-0 items-center gap-2">
          <code className="bg-muted min-w-0 flex-1 truncate rounded-md px-2 py-1 text-xs">
            {shareLink}
          </code>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => void copyShareLink()}
          >
            {copyState === 'copied' ? t('common.copied') : t('common.copy')}
          </Button>
          {copyState === 'failed' ? (
            <p className="text-destructive w-full text-xs">{t('export.copyFailed')}</p>
          ) : null}
        </CardContent>
      </Card>

      {/* Two columns from the narrowest screen: four short tab cards stacked cost
          a whole screen of scrolling before the page's own content appeared. */}
      <nav className="grid grid-cols-2 gap-3">
        {childRoutes.map((route) => (
          <Link
            key={route.to}
            to={route.to}
            className="focus-visible:ring-ring block min-w-0 rounded-xl focus-visible:ring-2 focus-visible:outline-none"
          >
            <Card className="hover:bg-accent/40 h-full transition-colors">
              {/* Tight inner spacing and a smaller label in the narrow
                  two-column layout, so the longest translated tab label still
                  fits on one line. */}
              <CardContent className="flex min-w-0 items-center gap-1.5 px-3 pt-4 sm:gap-3 sm:px-4 sm:pt-5">
                <route.icon className="text-primary size-4 shrink-0" aria-hidden />
                <span className="truncate text-xs font-medium sm:text-sm">{route.label}</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </nav>

      <Outlet context={{ event } satisfies EventDetailContextValue} />
    </div>
  )
}
